import { TaskData, TaskDocument } from '@shared/types';
import { OrderByClause, WhereClause } from '../queryBuilder';
import { useAuthenticatedSession } from '@/authenticatedSession/AuthenticatedSessionContext';
import { makeLoadState, useLoadState } from '@/hooks/useLoadState';
import { useCallback, useEffect } from 'react';

export function useTasks(props?: {
  where?: WhereClause<TaskData>[];
  orderBy?: OrderByClause<TaskData>[];
}) {
  const authSession = useAuthenticatedSession();
  const { loadState, setLoadState } = useLoadState<TaskDocument[]>({
    status: 'loading',
  });
  useEffect(() => {
    setLoadState({ status: 'loading' });
    if (!authSession) {
      return;
    }
    return authSession.db.subscribeToTasksChanges(
      (collectionSnapshot) => {
        setLoadState((previousLoadState) => {
          if (previousLoadState.status !== 'loaded') {
            return makeLoadState({
              status: 'loaded',
              data: collectionSnapshot.docs.map((docSnapshot) => ({
                ...docSnapshot.data(),
                id: docSnapshot.id,
              })),
            });
          }

          const previousById = new Map(
            previousLoadState.data.map((task) => [task.id, task]),
          );
          const changedIds = new Set(
            collectionSnapshot
              .docChanges()
              .filter((change) => change.type !== 'removed')
              .map((change) => change.doc.id),
          );
          // When an object hasn't changed, preserve the object so that it doesn't
          // trigger a re-render of any views that depend on it.
          const newData = collectionSnapshot.docs.map((doc) => {
            const previousEntry = previousById.get(doc.id);
            if (previousEntry && !changedIds.has(doc.id)) {
              return previousEntry;
            }
            return {
              ...doc.data(),
              id: doc.id,
            };
          });
          return makeLoadState({
            status: 'loaded',
            data: newData,
          });
        });
      },
      props?.where,
      props?.orderBy,
    );
  }, [JSON.stringify(props), authSession]);
  return loadState;
}

export function useTaskAdd() {
  const authSession = useAuthenticatedSession();
  return useCallback(
    async (data: TaskData) => {
      if (!authSession) {
        return;
      }
      return await authSession.db.addTask(data);
    },
    [authSession],
  );
}
